import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import { ArrowBack as BackIcon, Print as PrintIcon, PictureAsPdf as PdfIcon } from '@mui/icons-material';
import { pdf } from '@react-pdf/renderer';
import vehicleService from '../../services/vehicleService';
import { Vehicle } from '../../types';
import ContratoConsignacaoDocument from './ContratoConsignacaoDocument';
import ContratoConsignacaoPDFDoc from './ContratoConsignacaoPDF';

const ConsignacaoView: React.FC = () => {
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!vehicleId) return;
      try {
        setLoading(true);
        const data = await vehicleService.getVehicle(Number(vehicleId));
        setVehicle(data);
        if (!data.is_consignado) {
          setError('Este veículo não está cadastrado como consignado.');
        }
      } catch (e) {
        console.error(e);
        setError('Não foi possível carregar o veículo.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [vehicleId]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = async () => {
    if (!vehicle) return;
    setDownloadingPdf(true);
    try {
      const doc = <ContratoConsignacaoPDFDoc vehicle={vehicle} />;
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contrato-consignacao-${vehicle.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !vehicle) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error || 'Veículo não encontrado.'}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/contratos')}>Voltar</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      <Box
        className="no-print"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Button startIcon={<BackIcon />} onClick={() => navigate('/contratos')} variant="outlined">
          Voltar
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={downloadingPdf ? <CircularProgress size={18} /> : <PdfIcon />}
            onClick={handleDownloadPdf}
            variant="contained"
            color="primary"
            disabled={downloadingPdf}
          >
            {downloadingPdf ? 'Gerando...' : 'Baixar PDF'}
          </Button>
          <Button startIcon={<PrintIcon />} onClick={handlePrint} variant="outlined">
            Imprimir
          </Button>
        </Box>
      </Box>
      <Paper sx={{ p: 0, overflow: 'hidden' }}>
        <ContratoConsignacaoDocument vehicle={vehicle} />
      </Paper>
    </Box>
  );
};

export default ConsignacaoView;
